'use client';

import * as stylex from '@stylexjs/stylex';
import { Phone } from 'lucide-react';
import * as React from 'react';
import { use, useMemo } from 'react';
import PhoneInputLib, { getCountryCallingCode } from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import { Combobox } from '../../../../Combobox';
import { FieldControlContext } from '../Field';
import { InputGroup, InputGroupInputProps } from '../InputGroup';
import { PhoneNumberFlagCountryCodes } from './types';
import '../../../../../styles/tokens.stylex';
import { borderRadius, size, spacing } from '../../../../../styles/tokens.stylex';
import '../../../form.tokens.stylex';
import { formTokens } from '../../../form.tokens.stylex';
import { typography } from '../../../../../styles/typography';

// ─── Context ─────────────────────────────────────────────────────────────

type PhoneInputContextValue = {
  showCountrySelector: boolean;
  inputId?: string;
};
const PhoneInputContext = React.createContext<PhoneInputContextValue>({
  showCountrySelector: true,
  inputId: undefined
});

// ─── Types ────────────────────────────────────────────────────────────────

export type PhoneInputProps = Omit<React.ComponentProps<'input'>, 'onChange' | 'value' | 'ref'> & {
  defaultCountry?: PhoneNumberFlagCountryCodes;
  showCountrySelector?: boolean;
  countryCodes?: PhoneNumberFlagCountryCodes[];
  defaultByIpAddress?: boolean;
  disabled?: boolean;
  placeholder?: string;
  styles?: stylex.StyleXStyles;
  inputId?: string;
};
type CountrySelectProps = {
  disabled?: boolean;
  value: PhoneNumberFlagCountryCodes;
  options: {
    label: PhoneNumberFlagCountryCodes;
    value: PhoneNumberFlagCountryCodes | undefined;
  }[];
  onChange: (country: string) => void;
};
type FlagComponentProps = {
  country: PhoneNumberFlagCountryCodes;
  countryName: string;
};

// ─── FlagComponent (internal) ─────────────────────────────────────────────

function FlagComponent({
  country,
  countryName
}: FlagComponentProps) {
  const Flag = flags[country];
  return <span className="xlhn99x x7giv3 xoob0kj x1o47r4 x78zum5 xqwie1i">
      {Flag ? <Flag title={countryName} /> : null}
    </span>;
}

// ─── CountrySelector (internal, satisfies countrySelectComponent contract) ─

function CountrySelector({
  disabled,
  value: selectedCountry,
  options: countryList,
  onChange
}: CountrySelectProps) {
  const {
    showCountrySelector
  } = React.useContext(PhoneInputContext);
  const items = useMemo(() => !showCountrySelector ? [] : countryList.map(o => typeof o === 'string' ? o : o?.value).filter((v): v is PhoneNumberFlagCountryCodes => v != null), [countryList, showCountrySelector]);
  if (!showCountrySelector) return null;
  return <div className="x3ybb33 xt970qd x6s0dn4 x78zum5 x41045o x1qudamw">
      <Combobox.Root value={selectedCountry} onValueChange={v => v != null && onChange(v)} items={items} id={undefined}>
        <Combobox.TriggerButton disabled={disabled} aria-label="Country" styles={sx.countryTriggerButton} render={<button id="country-select" type="button" />}>
          {selectedCountry ? <FlagComponent country={selectedCountry} countryName={selectedCountry} /> : <span {...stylex.props(typography.small, sx.countryTriggerPlaceholder)}>
              Select country
            </span>}
        </Combobox.TriggerButton>
        <Combobox.Portal>
          <Combobox.Positioner>
            <Combobox.Popup>
              <Combobox.PopupInput placeholder="Search country..." />
              <Combobox.List>
                {(item: PhoneNumberFlagCountryCodes) => <Combobox.Item key={item} value={item} styles={sx.countryItem}>
                    <FlagComponent country={item} countryName={item} />
                    <span {...stylex.props(typography.small, sx.countryItemName)}>
                      {item}
                    </span>
                    <span {...stylex.props(typography.small, sx.countryItemDial)}>
                      +{getCountryCallingCode(item)}
                    </span>
                    <Combobox.ItemIndicator />
                  </Combobox.Item>}
              </Combobox.List>
            </Combobox.Popup>
          </Combobox.Positioner>
        </Combobox.Portal>
      </Combobox.Root>
    </div>;
}

// ─── PhoneInputControl (internal, inputComponent for library) ───────────────

function PhoneInputControl({
  inputId,
  ...props
}: InputGroupInputProps & {
  inputId?: string;
}) {
  return <>
      <InputGroup.Addon align={'inline-start'}>
        <Phone size="1em" aria-hidden />
      </InputGroup.Addon>
      <InputGroup.RawInput data-slot="input-group-control" {...props} id={inputId ?? props.id} styles={sx.inputControl} />
    </>;
}
// ─── PhoneInput (root) ────────────────────────────────────────────────────

function buildFlagsFromList(acc: Partial<Record<PhoneNumberFlagCountryCodes, (p: {
  title: string;
}) => React.JSX.Element>>, country: PhoneNumberFlagCountryCodes) {
  acc[country] = flags[country];
  return acc;
}
export function PhoneInput({
  styles,
  defaultCountry,
  showCountrySelector = true,
  countryCodes,
  defaultByIpAddress = false,
  disabled,
  placeholder,
  inputId,
  id,
  ...rest
}: PhoneInputProps) {
  void defaultByIpAddress;
  //const { geoResponse } = useIpAddress(defaultByIpAddress)
  const geoResponse: {
    countryCode?: PhoneNumberFlagCountryCodes;
  } = {
    countryCode: 'AU'
  };
  const fieldControl = use(FieldControlContext);
  const defaultCountryCode = useMemo(() => defaultCountry ?? geoResponse?.countryCode ?? 'AU', [defaultCountry, geoResponse?.countryCode]);
  const flagsObject = useMemo(() => {
    return countryCodes ? [...new Set([...countryCodes, defaultCountryCode])].reduce(buildFlagsFromList, {}) : flags;
  }, [countryCodes, defaultCountryCode]);
  const contextValue = useMemo(() => ({
    showCountrySelector
  }), [showCountrySelector]);
  const {
    value,
    onBlur,
    onValueChange,
    ref,
    name
  } = fieldControl;
  const finalInputId = inputId ?? id;
  return <PhoneInputContext.Provider value={contextValue}>
      <InputGroup.Root styles={[typography.body, sx.root, styles]}>
        <PhoneInputLib {...rest} name={name} onBlur={onBlur} onChange={onValueChange} ref={ref} flagComponent={FlagComponent} countrySelectComponent={CountrySelector} countrySelectProps={{
        id: `${id}-country-select`
      }} inputComponent={PhoneInputControl} focusInputOnCountrySelection={true} smartCaret={false} flags={flagsObject} defaultCountry={defaultCountryCode} numberInputProps={{
        inputId: finalInputId
      }} value={value} disabled={disabled} placeholder={placeholder} countries={countryCodes} international={false} className="x1o47r4 x78zum5 x1r8uery x1iyjqo2 xs83m0k x1szn6h9" />
      </InputGroup.Root>
    </PhoneInputContext.Provider>;
}

// ─── Styles ─────────────────────────────────────────────────────────────

const sx = {
  root: {
    kVQacm: "xb3r6kr",
    kjj79g: "x1szn6h9",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  countryTriggerButton: {
    ksu8eU: "xng3xce",
    kMzoRj: "xc342km",
    kOIVth: "x3ybb33",
    k8WAf4: "x17fzs4e",
    kGNEyG: "x6s0dn4",
    kWkggS: "xjbqb8w xoayurg x1wrerfi",
    k1xSpc: "x78zum5",
    kwRFfy: "x41045o",
    kZCmMZ: "x1qudamw",
    $$css: true
  },
  countryTriggerPlaceholder: {
    kMwMTN: "x143ll9a",
    $$css: true
  },
  countryItem: {
    kOIVth: "x3ybb33",
    $$css: true
  },
  countryItemName: {
    kCS8Yb: "x1r8uery",
    kzQI83: "x1iyjqo2",
    kmuXW: "xs83m0k",
    $$css: true
  },
  countryItemDial: {
    kMwMTN: "x143ll9a",
    $$css: true
  },
  inputControl: {
    ksu8eU: "xng3xce",
    kMzoRj: "xc342km",
    kg3NbH: "xeaj3vf",
    kWkggS: "xjbqb8w x14rb9jf x1n5bzlp xyftt0y",
    kCS8Yb: "x1r8uery",
    kzQI83: "x1iyjqo2",
    kmuXW: "xs83m0k",
    kayTVb: "x14yy4lh",
    k7Eaqz: "xeuugli",
    $$css: true
  }
};